import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Header } from '../../components/mte/shell';
import { HeroIllustration } from '../../components/mte/hero-asset-img';
import { apiFetch, ApiError } from '../../lib/auth-fetch';
import type { HeroId, HeroCell } from '../../components/mte/placeholders';
import styles from './ChildHub.module.css';

interface Child { childId: string; firstName: string; schoolLevel: 'CP'|'CE1'|'CE2'|'CM1'|'CM2'; heroId: HeroId | null; }

function ChildHub(): JSX.Element {
  const [profils, setProfils] = useState<Child[]>([]);
  const [probe, setProbe] = useState<Record<string, { hasDiag: boolean; lastAt: string|null }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string|null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<{ children: Child[] }>('/api/parent/children');
        if (cancelled) return;
        const list = data?.children ?? [];
        setProfils(list); setLoading(false);
        for (const c of list) {
          try {
            const diag = await apiFetch<{ diagnostics: { occurredAt?: string }[] }>(
              '/api/device/children/' + encodeURIComponent(c.childId) + '/diagnostics'
            );
            if (cancelled) return;
            const arr = diag?.diagnostics ?? [];
            setProbe(p => ({ ...p, [c.childId]: { hasDiag: arr.length>0, lastAt: arr[0]?.occurredAt ?? null } }));
          } catch { if (!cancelled) setProbe(p => ({ ...p, [c.childId]: { hasDiag: false, lastAt: null } })); }
        }
      } catch (e) {
        if (cancelled) return;
        setLoading(false); setError(e instanceof ApiError ? e.message : 'Impossible de charger le hub.');
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <Layout context="enfant" width="child"
      header={<Header context="enfant" user={
        <div className={styles.appBar}>
          <h1 className={styles.title}>Mon hub</h1>
          <Link to="/famille/accueil/" data-action="changer-profil" className={styles.switchLink}>Changer de profil</Link>
        </div>
      }/>}
      footer={<></>}
    >
      <main className={styles.main}>
        <p className={styles.eyebrow}>Choisis une activite</p>
        <h2 className={styles.lede}>Ton univers est pret. Que veux-tu faire aujourd&apos;hui ?</h2>
        {loading && <section className={styles.loadingSection} aria-busy="true" aria-live="polite"><p>Chargement...</p></section>}
        {error && <section className={styles.errorSection} role="alert"><p>{error}</p></section>}
        {!loading && !error && profils.length === 0 && (
          <section className={styles.emptySection}>
            <p>Aucun profil trouve. Cree-en un pour commencer.</p>
            <Link to="/onboarding-parent/" className={styles.cta}>Creer un profil</Link>
          </section>
        )}
        {!loading && !error && profils.length > 0 && (
          <section className={styles.childrenGrid} aria-label="Profils enfants">
            {profils.map((p) => {
              const heroId = (p.heroId ?? 'ninja') as HeroId;
              const hasHero = !!p.heroId;
              const hasDiag = probe[p.childId]?.hasDiag;
              return (
                <article key={p.childId} className={styles.childCard} data-child-id={p.childId}>
                  <div className={styles.childHero}>
                    <HeroIllustration cell={{ id: heroId, label: heroId, role: '', emoji: '', accent: '--accent-' + heroId, bgColor: '#888' } as unknown as HeroCell} pose="standing" />
                  </div>
                  <h3 className={styles.childName}>{p.firstName}</h3>
                  <p className={styles.childLevel}>{p.schoolLevel}</p>
                  <div className={styles.childActions}>
                    <Link to={'/enfant/diagnostic/?childId=' + encodeURIComponent(p.childId)} className={styles.ctaPrimary} data-action={hasDiag ? 'reprendre-diagnostic' : 'commencer-diagnostic'}>
                      {hasDiag ? 'Reprendre mon diagnostic' : 'Commencer mon diagnostic'}
                    </Link>
                    {hasHero ? (
                      <Link to={'/enfant/mission/rentree/?childId=' + encodeURIComponent(p.childId)} className={styles.ctaSecondary} data-action="continuer-mission">Continuer la mission</Link>
                    ) : (
                      <Link to={'/onboarding/choix-heros/?childId=' + encodeURIComponent(p.childId)} className={styles.ctaSecondary} data-action="choisir-heros">Choisir un heros</Link>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
        <section className={styles.bottomNote}>
          <Link to="/famille/accueil/" className={styles.addChildLink}>+ Ajouter un autre enfant</Link>
        </section>
      </main>
    </Layout>
  );
}
export default ChildHub;
